import { useCallback, useEffect, useState } from 'react'

import { FileText, Xmark } from '@gravity-ui/icons'
import { Alert, Button, Modal, Spinner, toast } from '@heroui/react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'

import { type ReconciliationResult,reportsApi } from '@/api/reports.api'

interface ReconciliationDialogProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
}

export function ReconciliationDialog({ isOpen, onOpenChange }: ReconciliationDialogProps) {
  const { t } = useTranslation()
  const [file, setFile] = useState<File | null>(null)
  const [isReconciling, setIsReconciling] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [result, setResult] = useState<ReconciliationResult | null>(null)

  // Reset state when dialog is closed
  useEffect(() => {
    if (!isOpen) {
      setFile(null)
      setResult(null)
    }
  }, [isOpen])

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0])
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
  })

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const handleReconcile = useCallback(async () => {
    if (!file) return
    setIsReconciling(true)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await reportsApi.reconcileReport(formData)
      setResult(response)

      toast.success(t('reports.reconciliation_dialog.success_title'), {
        description: t('reports.reconciliation_dialog.success_desc'),
      })
    } catch (error) {
      console.error('Reconciliation failed:', error)
      toast.danger(t('reports.reconciliation_dialog.error_title'), {
        description: (error as Error).message || t('reports.reconciliation_dialog.error_desc'),
      })
    } finally {
      setIsReconciling(false)
    }
  }, [file, t])

  const handleDownload = useCallback(async () => {
    if (!result || !file) return
    setIsDownloading(true)
    try {
      const blob = await reportsApi.downloadReconciledFile(result.DownloadUrl)

      const originalName = file.name
      const dotIndex = originalName.lastIndexOf('.')
      let newName = ''
      if (dotIndex !== -1) {
        newName = `${originalName.substring(0, dotIndex)}_checked${originalName.substring(dotIndex)}`
      } else {
        newName = `${originalName}_checked`
      }

      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.setAttribute('download', newName)
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      console.error('File download failed:', error)
      toast.danger('Ошибка при скачивании файла', {
        description: (error as Error).message,
      })
    } finally {
      setIsDownloading(false)
    }
  }, [result, file])

  const handleCancel = useCallback(() => {
    setFile(null)
    setResult(null)
    onOpenChange(false)
  }, [onOpenChange])

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <Modal.Backdrop variant="blur" className="bg-zinc-950/40" isDismissable>
        <Modal.Container placement="center">
          <Modal.Dialog>
            <Modal.CloseTrigger />
            <Modal.Header>
              <Modal.Heading className="font-bold">
                {result
                  ? t('reports.reconciliation_dialog.results_title')
                  : t('reports.reconciliation_dialog.title')}
              </Modal.Heading>
            </Modal.Header>
            <Modal.Body className="mt-2 space-y-4">
              {result ? (
                <div className="space-y-4">
                  {/* File card */}
                  <div className="border border-zinc-200 dark:border-zinc-800 rounded-2xl p-4 bg-zinc-50/30 dark:bg-zinc-900/10 flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-primary/10 text-primary flex-shrink-0">
                      <FileText width={20} height={20} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate text-zinc-800 dark:text-zinc-200">
                        {file?.name}
                      </p>
                      <p className="text-xs text-zinc-500 dark:text-zinc-500">
                        {file ? formatBytes(file.size) : ''}
                      </p>
                    </div>
                  </div>

                  {/* Stats grid */}
                  <div className="grid grid-cols-3 gap-3">
                    {/* Total docs card */}
                    <div className="flex flex-col items-center justify-center p-4 rounded-2xl border border-zinc-100 dark:border-zinc-800 bg-zinc-50/20 dark:bg-zinc-900/5 text-center">
                      <span className="text-2xl font-bold text-zinc-800 dark:text-zinc-200">
                        {result.Total}
                      </span>
                      <span className="text-[10px] text-zinc-500 mt-1 uppercase tracking-wider font-semibold">
                        {t('reports.reconciliation_dialog.total_docs')}
                      </span>
                    </div>

                    {/* Matched docs card */}
                    <div className="flex flex-col items-center justify-center p-4 rounded-2xl border border-success-200/50 dark:border-success-900/20 bg-success-50/10 dark:bg-success-950/5 text-center">
                      <span className="text-2xl font-bold text-success">
                        {result.Matched}
                      </span>
                      <span className="text-[10px] text-success/80 mt-1 uppercase tracking-wider font-semibold">
                        {t('reports.reconciliation_dialog.matched_docs')}
                      </span>
                    </div>

                    {/* Not matched docs card */}
                    <div className="flex flex-col items-center justify-center p-4 rounded-2xl border border-danger-200/50 dark:border-danger-900/20 bg-danger-50/10 dark:bg-danger-950/5 text-center">
                      <span className="text-2xl font-bold text-danger">
                        {result.NotMatched}
                      </span>
                      <span className="text-[10px] text-danger/80 mt-1 uppercase tracking-wider font-semibold">
                        {t('reports.reconciliation_dialog.not_matched_docs')}
                      </span>
                    </div>
                  </div>
                </div>
              ) : file ? (
                <div className="space-y-3">
                  <div className="border border-zinc-200 dark:border-zinc-800 rounded-2xl p-4 bg-zinc-50/30 dark:bg-zinc-900/10 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2.5 rounded-xl bg-primary/10 text-primary flex-shrink-0">
                        <FileText width={20} height={20} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate text-zinc-800 dark:text-zinc-200">
                          {file.name}
                        </p>
                        <p className="text-xs text-zinc-500 dark:text-zinc-500">
                          {formatBytes(file.size)}
                        </p>
                      </div>
                    </div>
                    <Button
                      isIconOnly
                      variant="ghost"
                      size="sm"
                      onPress={() => setFile(null)}
                      aria-label="Remove file"
                      className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                      isDisabled={isReconciling}
                    >
                      <Xmark width={16} height={16} />
                    </Button>
                  </div>
                  <Alert status="warning" className="rounded-xl border border-warning/10">
                    <Alert.Indicator />
                    <Alert.Content>
                      <Alert.Description className="text-xs">
                        {t('reports.reconciliation_dialog.response_hint')}
                      </Alert.Description>
                    </Alert.Content>
                  </Alert>
                </div>
              ) : (
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-2xl p-8 flex flex-col items-center justify-center cursor-pointer transition-all duration-200 min-h-[200px] ${
                    isDragActive
                      ? 'border-primary bg-primary/5'
                      : 'border-zinc-200 dark:border-zinc-800 hover:border-zinc-400 dark:hover:border-zinc-600 bg-zinc-50/50 dark:bg-zinc-900/30'
                  }`}
                >
                  <input {...getInputProps()} />
                  <div className="flex flex-col items-center text-center space-y-3">
                    <div
                      className={`p-4 rounded-full ${
                        isDragActive
                          ? 'bg-primary/10 text-primary'
                          : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400'
                      }`}
                    >
                      <FileText width={28} height={28} />
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                        {isDragActive
                          ? t('reports.reconciliation_dialog.drag_active')
                          : t('reports.reconciliation_dialog.description')}
                      </p>
                      <p className="text-xs text-zinc-400 dark:text-zinc-500">
                        {t('reports.reconciliation_dialog.format_hint')}
                      </p>
                    </div>
                    <Button variant="secondary" size="sm" className="mt-2" type="button" onPress={open}>
                      {t('reports.reconciliation_dialog.select_file')}
                    </Button>
                  </div>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer className="flex gap-3 justify-end mt-6">
              {result ? (
                <>
                  <Button variant="secondary" onClick={handleCancel} isDisabled={isDownloading}>
                    {t('reports.reconciliation_dialog.done')}
                  </Button>
                  <Button variant="primary" onClick={handleDownload} isDisabled={isDownloading}>
                    {isDownloading ? (
                      <div className="flex items-center gap-2">
                        <Spinner size="sm" color="current" />
                        <span>{t('common.loading')}</span>
                      </div>
                    ) : (
                      t('reports.reconciliation_dialog.download_checked')
                    )}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" onClick={handleCancel} isDisabled={isReconciling}>
                    {t('common.cancel')}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleReconcile}
                    isDisabled={!file || isReconciling}
                  >
                    {isReconciling ? (
                      <div className="flex items-center gap-2">
                        <Spinner size="sm" color="current" />
                        <span>{t('reports.reconciliation_dialog.reconciling')}</span>
                      </div>
                    ) : (
                      t('reports.reconciliation_dialog.reconcile')
                    )}
                  </Button>
                </>
              )}
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  )
}
